import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Save, Eye, Globe, Upload, Plus, Trash2,
  GripVertical, Image, Video, Music, Calendar, Mail,
  User, Layout, Palette, Settings, ExternalLink, Newspaper, Copy, Quote, Star,
  Briefcase, Users, HelpCircle, MessageSquare, Phone, CheckCircle2, XCircle, AlertTriangle, Link, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import CloudinaryUploader, { GalleryUploader } from '@/components/CloudinaryUploader';

export const TestimonialsModuleEditor = ({ data, onChange, siteSlug }) => {
  const testimonials = data.testimonials || [];
  const layout = data.layout || 'grid';
  const [editingIdx, setEditingIdx] = useState(null);
  
  const addTestimonial = () => {
    const newTestimonial = { id: Date.now().toString(), quote: '', author: '', role: '', company: '', image: '', rating: 5 };
    onChange({...data, testimonials: [...testimonials, newTestimonial]});
    setEditingIdx(testimonials.length);
  };
  
  const updateTestimonial = (idx, updates) => {
    const updated = [...testimonials];
    updated[idx] = {...updated[idx], ...updates};
    onChange({...data, testimonials: updated});
  };
  
  const removeTestimonial = (idx) => {
    onChange({...data, testimonials: testimonials.filter((_, i) => i !== idx)});
    setEditingIdx(null);
  };

  const renderEditor = (item, idx) => (
    <div className="bg-slate-800 border border-slate-600 rounded-lg p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-white">Edit Testimonial</h4>
        <Button variant="ghost" size="sm" onClick={() => setEditingIdx(null)} className="text-slate-400 hover:text-white">Done</Button>
      </div>
      
      <div className="space-y-2">
        <Label>Quote/Testimonial</Label>
        <Textarea value={item.quote} onChange={(e) => updateTestimonial(idx, { quote: e.target.value })} placeholder="What did they say?" rows={3} />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Author Name</Label>
          <Input value={item.author || ''} onChange={(e) => updateTestimonial(idx, { author: e.target.value })} placeholder="John Smith" />
        </div>
        <div className="space-y-2">
          <Label>Role/Title</Label>
          <Input value={item.role || ''} onChange={(e) => updateTestimonial(idx, { role: e.target.value })} placeholder="e.g. Student" />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Company/Organisation (optional)</Label>
        <Input value={item.company || ''} onChange={(e) => updateTestimonial(idx, { company: e.target.value })} placeholder="e.g. Royal Academy" />
      </div>
      
      <div className="space-y-2">
        <Label>Photo (optional)</Label>
        <CloudinaryUploader
          currentImage={item.image}
          onUpload={(url) => updateTestimonial(idx, { image: url })}
          onRemove={() => updateTestimonial(idx, { image: '' })}
          folder={`sites/${siteSlug}/testimonials`}
          aspectRatio="aspect-square"
          placeholder="Drop photo"
        />
      </div>
      
      <div className="space-y-2">
        <Label>Rating</Label>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button key={star} type="button" onClick={() => updateTestimonial(idx, { rating: star })} className={cn("w-8 h-8 rounded transition-colors", star <= (item.rating || 5) ? "text-amber-400 hover:text-amber-300" : "text-slate-500 hover:text-slate-400")}>
              <Star className="w-6 h-6 fill-current" />
            </button>
          ))}
        </div>
      </div>

      <div className="pt-2 border-t border-slate-700">
        <Button variant="ghost" size="sm" onClick={() => removeTestimonial(idx)} className="text-red-400 hover:text-red-300 hover:bg-red-500/20">
          <Trash2 className="w-4 h-4 mr-2" /> Delete Testimonial
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Section Title</Label>
        <Input value={data.title || 'Testimonials'} onChange={(e) => onChange({...data, title: e.target.value})} />
      </div>

      <div className="space-y-3">
        <Label>Display Layout</Label>
        <div className="flex items-center gap-2">
          {['grid', 'carousel', 'masonry'].map(l => (
            <button key={l} onClick={() => onChange({...data, layout: l})} className={cn("flex flex-col items-center gap-1 px-4 py-3 rounded-lg border transition-all capitalize", layout === l ? "bg-orange-500/20 border-orange-500 text-orange-400" : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500")}>
              <span className="text-xs">{l}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Testimonials ({testimonials.length})</Label>
          <Button variant="outline" size="sm" onClick={addTestimonial}><Plus className="w-4 h-4 mr-1" /> Add Testimonial</Button>
        </div>
        
        {testimonials.length === 0 ? (
          <div className="text-center py-12 bg-slate-700/30 rounded-lg border border-slate-600">
            <MessageSquare className="w-10 h-10 mx-auto text-slate-500 mb-3" />
            <p className="text-sm text-slate-400">No testimonials yet. Add reviews from clients!</p>
          </div>
        ) : editingIdx !== null && testimonials[editingIdx] ? (
          renderEditor(testimonials[editingIdx], editingIdx)
        ) : (
          <div className="space-y-2">
            {testimonials.map((item, idx) => (
              <div key={item.id || idx} onClick={() => setEditingIdx(idx)} className="flex items-center gap-4 p-3 bg-slate-700/30 border border-slate-600 rounded-lg cursor-pointer hover:bg-slate-700/50 hover:border-slate-500 transition-all group">
                <div className="w-10 h-10 rounded-full bg-slate-600 overflow-hidden flex-shrink-0">
                  {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Quote className="w-4 h-4 text-slate-500" /></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-white truncate text-sm">"{item.quote?.substring(0, 40) || 'No quote'}..."</h4>
                  <p className="text-xs text-slate-400">{item.author || 'Anonymous'}{item.role ? ` - ${item.role}` : ''}</p>
                </div>
                <div className="flex items-center gap-0.5 text-amber-400">
                  {[...Array(item.rating || 5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-current" />)}
                </div>
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeTestimonial(idx); }} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 hover:bg-red-500/20 h-8 w-8 p-0"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Module icons
const moduleIcons = {
  home: Layout,
  about: User,
  gallery: Image,
  video_gallery: Video,
  news: Newspaper,
  events: Calendar,
  contact: Mail,
  music: Music,
  social_links: ExternalLink,
  mailing_list: Mail,
  press: Quote,
  services: Briefcase,
  team: Users,
  faq: HelpCircle,
  testimonials: MessageSquare,
};


